import { useRef, useState } from 'react';
import { replaceDocument } from '@/lib/database';
import type { Tables } from '@/types/database.types';

type DocumentRow = Tables<'documents'>;

// File types accepted for document upload / replace. Shared so the project
// documents section and the workspace documents page stay in lockstep.
export const ACCEPTED_DOCUMENT_TYPES = '.pdf,.docx,.xlsx,.xls,.txt,.md,.csv,.pptx,.doc';

/**
 * Shared wiring for "replace a document's file": a hidden file input, the
 * per-row in-flight state, and the replaceDocument call. Both the project
 * documents section and the workspace-wide documents page use this so the
 * interaction pattern lives in exactly one place.
 *
 * Usage:
 *   const { replacingId, inputRef, startReplace, onFileSelected } =
 *     useReplaceDocument((updated) => setDocs(prev => prev.map(d =>
 *       d.id === updated.id ? { ...d, ...updated } : d)));
 *
 *   <Button onClick={() => startReplace(doc.id)} disabled={replacingId === doc.id} />
 *   <input ref={inputRef} type="file" accept={ACCEPTED_DOCUMENT_TYPES}
 *          onChange={onFileSelected} className="hidden" />
 *
 * `onReplaced` receives the updated row; callers own their list state (which
 * may carry extra joined fields like project_name), so merge rather than
 * replace if needed.
 */
export function useReplaceDocument(onReplaced: (updated: DocumentRow) => void) {
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const targetIdRef = useRef<string | null>(null);

  const startReplace = (docId: string) => {
    targetIdRef.current = docId;
    inputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const docId = targetIdRef.current;
    if (!file || !docId) return;
    try {
      setReplacingId(docId);
      const updated = await replaceDocument(docId, file);
      onReplaced(updated);
    } catch (error) {
      console.error('Failed to replace document:', error);
    } finally {
      setReplacingId(null);
      targetIdRef.current = null;
      // Reset so selecting the same file again still fires onChange.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return { replacingId, inputRef, startReplace, onFileSelected };
}
